import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Download, FileSignature, PieChart, RefreshCw } from 'lucide-react';
import { Button } from '../ui/button';
import { BACKEND_URL } from './constants';
import { getToken } from '../../services/tokenService';
import CourbeMensuelle from './CourbeMensuelle';
import EnvoiComptable from './EnvoiComptable';

const eur = (v) => (Number(v) || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
const debutAnnee = () => `${new Date().getFullYear()}-01-01`;
const aujourdhui = () => new Date().toISOString().slice(0, 10);

function BlocDispositif({ g }) {
  return (
    <div className="border rounded-xl overflow-hidden" data-testid={`recap-dispositif-${g.dispositif}`}>
      <div className="flex items-center justify-between px-4 py-2.5 bg-orange-50 border-b">
        <span className="font-semibold text-gray-800">{g.dispositif}</span>
        <span className="text-sm text-gray-600">{g.nb_recus} reçu(s) — <strong className="text-orange-700">{eur(g.total)}</strong></span>
      </div>
      <table className="w-full text-sm">
        <tbody>
          {g.base > 0 && <tr className="border-b"><td className="px-4 py-1.5 font-medium">Montant de base (reçus détaillés)</td><td /><td className="px-4 py-1.5 text-right font-medium">{eur(g.base)}</td></tr>}
          {g.contributions.map(c => (
            <tr key={c.nom} className="border-b last:border-0">
              <td className="px-4 py-1.5 text-gray-700">{c.nom}</td>
              <td className="px-4 py-1.5 text-xs text-gray-400 text-right">{c.nb_recus} reçu(s)</td>
              <td className="px-4 py-1.5 text-right">{eur(c.montant)}</td>
            </tr>
          ))}
          {g.sans_detail > 0 && <tr><td className="px-4 py-1.5 text-gray-500 italic">Reçus sans détail des contributions</td><td /><td className="px-4 py-1.5 text-right text-gray-500">{eur(g.sans_detail)}</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

export default function RecapContributions() {
  const [debut, setDebut] = useState(debutAnnee());
  const [fin, setFin] = useState(aujourdhui());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [edition, setEdition] = useState(false);
  const headers = { Authorization: `Bearer ${getToken()}` };
  const qs = `debut=${debut}&fin=${fin}`;

  const charger = useCallback(async () => {
    setLoading(true);
    try { setData((await axios.get(`${BACKEND_URL}/api/recus-ess/recap-contributions?${qs}`, { headers })).data); }
    catch { toast.error('Erreur chargement du récapitulatif'); }
    finally { setLoading(false); }
  }, [qs]);

  useEffect(() => { charger(); }, [charger]);

  const exporter = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/recus-ess/recap-contributions?${qs}&format=csv`, { headers, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a'); a.href = url; a.download = `recap_contributions_${debut}_${fin}.csv`; a.click();
    } catch { toast.error('Erreur export CSV'); }
  };

  const editerPdf = async () => {
    setEdition(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/recus-ess/recap-contributions?${qs}&format=pdf`, { headers, responseType: 'blob', timeout: 120000 });
      const nom = (res.headers['content-disposition'] || '').match(/filename="([^"]+)"/)?.[1] || `recap_contributions_${debut}_${fin}.pdf`;
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a'); a.href = url; a.download = nom; a.click();
      toast.success('PDF officiel édité et classé dans la GED');
    } catch { toast.error("Erreur lors de l'édition du PDF"); }
    finally { setEdition(false); }
  };

  const inp = 'px-3 py-2 border border-gray-300 rounded-lg text-sm';
  return (
    <div className="bg-white rounded-xl border p-5 space-y-4" data-testid="recap-contributions">
      <div className="flex flex-wrap items-end gap-3">
        <h4 className="font-semibold text-gray-800 flex items-center gap-2 mr-auto"><PieChart className="h-4 w-4 text-orange-500" /> Récapitulatif des contributions encaissées</h4>
        <label className="text-xs text-gray-500">Du<input type="date" value={debut} onChange={e => setDebut(e.target.value)} className={`${inp} block`} data-testid="recap-debut" /></label>
        <label className="text-xs text-gray-500">Au<input type="date" value={fin} onChange={e => setFin(e.target.value)} className={`${inp} block`} data-testid="recap-fin" /></label>
        <Button variant="outline" onClick={charger} disabled={loading} data-testid="recap-actualiser"><RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Actualiser</Button>
        <Button onClick={exporter} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="recap-export-csv"><Download className="h-4 w-4 mr-1" /> Export CSV</Button>
        <Button onClick={editerPdf} disabled={edition} className="bg-[#1e3a5f] hover:bg-[#16304f] text-white" data-testid="recap-export-pdf">
          {edition ? <RefreshCw className="h-4 w-4 mr-1 animate-spin" /> : <FileSignature className="h-4 w-4 mr-1" />} PDF officiel signé
        </Button>
      </div>
      <EnvoiComptable debut={debut} fin={fin} />
      {data && (
        <>
          <div className="flex gap-6 text-sm" data-testid="recap-total">
            <span>Reçus : <strong>{data.nb_recus}</strong></span>
            <span>Total encaissé : <strong className="text-orange-700">{eur(data.total)}</strong></span>
          </div>
          {data.dispositifs.length === 0
            ? <p className="text-sm text-gray-400" data-testid="recap-vide">Aucun reçu ESS sur cette période.</p>
            : <div className="grid md:grid-cols-2 gap-4">{data.dispositifs.map(g => <BlocDispositif key={g.dispositif} g={g} />)}</div>}
          <CourbeMensuelle debut={debut} fin={fin} />
        </>
      )}
    </div>
  );
}
